/**
 * Оплата по СБП — платёж ДО заказа, а заказ оформляет сервер.
 *
 * Карточный порядок обратный: заявка создаётся, и уже она удерживает деньги на карте. По СБП так
 * нельзя. Человек уходит платить в приложение своего банка и возвращается когда угодно — через
 * минуту, через десять, или не возвращается вовсе. Заявка, созданная авансом под такой платёж,
 * жила бы в подвешенном состоянии неизвестно сколько, поэтому сначала берутся деньги, а заявка
 * оформляется на уже полученные.
 *
 * **Оформляет её сервер, а не клиент.** Раньше заказ создавал клиент, дождавшись статуса «оплачен».
 * Ждал он ровно пять минут; банк однажды подтвердил оплату на 31 секунду позже — вкладка к тому
 * времени ушла с экрана, деньги списались, а заказа не появилось (прод, 08.09.2026). Теперь
 * описание заказа уезжает на сервер вместе с запросом кода (`order` в `startSbpPayment`), и
 * сервер оформляет заказ сам, когда банк ответит, — даже если вкладка давно закрыта. Клиент
 * только ждёт, пока в статусе платежа появится `order_id`, и показывает этот заказ.
 *
 * Сумму называет сервер: она считается по тем же правилам, что и `POST /quote`, и клиент её не
 * складывает.
 */
import { apiRequest } from '@/shared/api/client';
import type { OrderDraft } from './model/draft';
import { TIME_MODE_WIRE } from './model/draft';
import { orderRequestBody, tripCars } from './quote';

/** Статусы платежа, как их присылает сервер. */
export type SbpPaymentStatus = 'pending' | 'paid' | 'declined';

export interface StartedSbpPayment {
  id: string;
  /**
   * Ссылка платёжного кода НСПК (`https://qr.nspk.ru/…`).
   *
   * Ею живут оба способа заплатить: на телефоне она открывает приложение банка, на компьютере из
   * неё же рисуется код, который сканируют телефоном.
   */
  payload: string;
  /** Готовая картинка кода от шлюза. `null` — не беда: ссылка выше работает сама по себе. */
  qr_image_base64: string | null;
  amount: string;
  /** Срок жизни кода, RFC 3339 — около пятнадцати минут. После него платить по нему нельзя. */
  expires_at: string;
}

export interface SbpPaymentState {
  id: string;
  status: SbpPaymentStatus;
  amount: string;
  failure_reason: string | null;
  /**
   * Заказ, который сервер оформил на этот платёж. Появляется после `paid`, и именно он — а не
   * статус — означает «готово»: `paid` без заказа значит «деньги получены, оформляем».
   */
  order_id: string | null;
  expires_at: string;
}

/**
 * Заводит платёж и получает код.
 *
 * `latitude`/`longitude`/`cars`/`delivery_speed` — то же описание выезда, что уходит в
 * `POST /quote`: сервер считает сумму сам, ровно один раз. `order` — описание заказа целиком, тем
 * же билдером, что собирает карточный `POST /orders`: по нему сервер оформит заказ, когда банк
 * подтвердит оплату. Способа оплаты в нём нет — им и является этот платёж.
 */
export function startSbpPayment(draft: OrderDraft): Promise<StartedSbpPayment> {
  return apiRequest<StartedSbpPayment>('/v1/consumer/orders/sbp', {
    method: 'POST',
    body: {
      latitude: draft.latitude,
      longitude: draft.longitude,
      delivery_speed: TIME_MODE_WIRE[draft.timeMode],
      cars: tripCars(draft),
      order: orderRequestBody(draft),
    },
  });
}

export function fetchSbpPayment(paymentId: string): Promise<SbpPaymentState> {
  return apiRequest<SbpPaymentState>(`/v1/consumer/orders/sbp/${paymentId}`);
}

/** Как часто спрашивать сервер. */
export const SBP_POLL_INTERVAL_MS = 3000;

/**
 * Сколько ждать банк ПОСЛЕ того, как код истёк.
 *
 * Подтверждение доходит до нас не в ту же секунду, что до банка, и человек, заплативший за десять
 * секунд до конца срока, не должен услышать «банк не подтвердил».
 */
export const SBP_EXPIRY_GRACE_MS = 60 * 1000;

/**
 * Дедлайн на случай, если сервер так и не назвал `expires_at` — все запросы упали.
 *
 * Пятнадцать минут — срок кода, плюс тот же запас. Дольше ждать нечего: код к тому времени истёк.
 */
export const SBP_FALLBACK_TIMEOUT_MS = 15 * 60 * 1000 + SBP_EXPIRY_GRACE_MS;

export interface AwaitSbpOptions {
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  /**
   * Каждый ответ сервера по дороге — в том числе «оплачен, оформляем». Экран по нему меняет
   * подпись, чтобы человек, у которого уже списали деньги, не читал «ждём оплаты».
   */
  onProgress?: (state: SbpPaymentState) => void;
}

/**
 * Ждёт, пока сервер оформит заказ на платёж, опрашивая его.
 *
 * Опрос, а не ожидание возврата человека на страницу: из приложения банка на наш сайт он
 * возвращается не всегда, а на компьютере — не возвращается никогда, потому что платил с телефона.
 * Единственный надёжный источник — сервер.
 *
 * Три исхода:
 * - состояние с `order_id` — заказ оформлен, дальше показываем его;
 * - состояние `declined` — банк отказал, причина в `failure_reason`;
 * - `null` — срок кода вышел, а оплата так и не подтверждена. Дедлайн задаёт сервер: `expires_at`
 *   плюс запас; пока сервер его не назвал, действует запасной.
 *
 * `paid` без `order_id` не исход, а этап: деньги получены, сервер оформляет заказ. С этого момента
 * дедлайна нет вовсе — сервер закончит либо заказом, либо отказом с возвратом, и оба придут
 * сюда же. Сдаться и вернуть человека к кнопке «Оформить и оплатить» значит предложить ему
 * заплатить второй раз за тот же заказ.
 *
 * Ошибка сети не прекращает ожидание: деньги уже могли уйти, и бросить наблюдение из-за одного
 * неудачного запроса значит оставить человека без заявки при списанных деньгах. Первый запрос
 * уходит всегда, даже если срок кода давно прошёл: при возврате на экран спустя час это
 * единственный способ узнать, что заказ давно оформлен.
 */
export async function awaitSbpPayment(
  paymentId: string,
  signal: AbortSignal,
  options: AwaitSbpOptions = {},
): Promise<SbpPaymentState | null> {
  const now = options.now ?? (() => Date.now());
  const sleep =
    options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));

  let deadline = now() + SBP_FALLBACK_TIMEOUT_MS;
  let paid = false;

  while (!signal.aborted) {
    try {
      const state = await fetchSbpPayment(paymentId);
      if (state.order_id !== null || state.status === 'declined') return state;

      options.onProgress?.(state);
      const expiresAt = Date.parse(state.expires_at);
      if (Number.isFinite(expiresAt)) deadline = expiresAt + SBP_EXPIRY_GRACE_MS;
      if (state.status === 'paid') paid = true;
    } catch {
      // Тишина здесь намеренная: следующий круг спросит снова.
    }

    if (!paid && now() >= deadline) return null;
    await sleep(SBP_POLL_INTERVAL_MS);
  }

  return null;
}

/** Подпись на экране ожидания, когда деньги уже получены и сервер оформляет заказ. */
export const SBP_PLACING_TEXT = 'Оплата подтверждена, оформляем заказ…';

/**
 * Подпись, пока спрашиваем сервер, чем кончился платёж, — после «Отменить» и после срока.
 *
 * Фраза общая для всех клиентов и записана дословно: пока ответ в пути, экран обязан говорить
 * ровно это и не показывать ни кода, ни живой кнопки оплаты. Это и есть окно двойной оплаты.
 */
export const SBP_CHECKING_TEXT = 'Проверяем оплату…';

/** Деньги у нас, заказа ещё нет: он появится сам, и человеку надо знать где. */
export const SBP_SETTLING_TEXT = 'Оплата прошла, оформляем заказ — он появится на главной.';

/**
 * «Отменить» по коду, за который ещё не платили.
 *
 * С 11.09.2026 это ровно отмена, и сказано прямо: уходя с экрана, страница гасит код у банка
 * (`dismissSbpPayment`), платить по нему уже нечем. Прежняя уклончивая фраза — «если деньги всё
 * же спишутся, заказ оформится сам» — была следствием того, что код продолжал жить ещё до
 * пятнадцати минут; причина убрана, вместе с ней и уклончивость. Слова владельца про прежнее
 * поведение: «человек хотел оплатить, закрыл, всё».
 */
export const SBP_CANCELED_TEXT = 'Оплата отменена.';

/**
 * `POST /v1/consumer/orders/sbp/{id}/dismiss` — человек ушёл с экрана оплаты, не заплатив.
 *
 * Ошибку глотает намеренно: человек уже уходит, и держать его здесь из-за неудавшейся отмены
 * неправильно — код всё равно истечёт сам.
 */
export async function dismissSbpPayment(paymentId: string): Promise<void> {
  try {
    await apiRequest<void>(`/v1/consumer/orders/sbp/${paymentId}/dismiss`, { method: 'POST' });
  } catch {
    // Намеренно молча — см. доку выше.
  }
}

/**
 * Спросить не вышло — а значит, мы не знаем, ушли деньги или нет.
 *
 * Формулировка намеренно не обещает ни того, ни другого: единственное «неизвестно» в этом наборе,
 * и признаться в нём честнее, чем предложить заплатить второй раз.
 */
export const SBP_UNKNOWN_TEXT =
  'Не удалось проверить оплату. Если деньги списались, заказ появится на главной сам.';

/**
 * Чем кончился платёж — ответом СЕРВЕРА, а не догадкой экрана.
 *
 * Правило одно на все клиенты: оплата закончилась любым способом — успехом, отказом, отменой,
 * уходом с экрана, истёкшим сроком — и первым делом мы спрашиваем сервер о её судьбе. Есть заказ
 * — ведём в заказ; нет — на главную и объясняем.
 *
 * До 09.09.2026 клиент этого не делал: «Отменить» и истёкший срок возвращали на сводку с живой
 * кнопкой «Оформить и оплатить». А заказ по платежу с этого дня оформляет сервер сам, и делает
 * это уже после того, как вкладка ушла с экрана. Человек с оплаченным, но ещё не оформленным
 * заказом видел приглашение заплатить второй раз.
 */
export type SbpOutcome =
  /** Заказ оформлен — дальше только он. */
  | { kind: 'placed'; orderId: string; amount: string }
  /** Деньги у нас, заказ ещё оформляется. Возвращать к оплате нельзя ни в каком виде. */
  | { kind: 'settling'; text: string }
  /**
   * Ничего не списано, и код ещё жив.
   *
   * «Жив» здесь не значит «можно вернуться к сводке». Код лежит у банка до пятнадцати минут, и
   * заплатить по нему могут уже после того, как эта вкладка ушла с экрана; живая кнопка «Оформить
   * и оплатить» рядом с таким кодом заводит ВТОРОЙ платёж по тому же заказу. Поэтому и отсюда
   * уходят на главную — см. `SBP_CANCELED_TEXT`.
   */
  | { kind: 'open' }
  /** Отказ или истёкший срок: заказа не будет, деньги, если ушли, вернутся. */
  | { kind: 'failed'; text: string }
  /**
   * Спросить не вышло.
   *
   * Отдельно от `settling`, хотя ведут себя они почти одинаково: к оплате не возвращаем ни там,
   * ни там, но выбрасывать черновик здесь нельзя. «Деньги получены» — это знание, а тут его нет,
   * и упавший на минуту сервер не повод стереть набранный руками адрес.
   */
  | { kind: 'unknown'; text: string };

export async function resolveSbpOutcome(
  paymentId: string,
  now: number = Date.now(),
): Promise<SbpOutcome> {
  let state: SbpPaymentState;
  try {
    state = await fetchSbpPayment(paymentId);
  } catch {
    // Молчание сервера — не «денег нет». Из двух ошибок здесь дорого стоит ровно одна:
    // предложить заплатить ещё раз по уже оплаченному.
    return { kind: 'unknown', text: SBP_UNKNOWN_TEXT };
  }

  if (state.order_id !== null) {
    return { kind: 'placed', orderId: state.order_id, amount: state.amount };
  }
  if (state.status === 'paid') return { kind: 'settling', text: SBP_SETTLING_TEXT };
  if (state.status === 'declined') return { kind: 'failed', text: sbpFailureText(state) };

  // Код ещё жив — платить по нему можно, и ничего пока не произошло. Тот же запас, что и у
  // ожидания: подтверждение доходит до нас не в ту же секунду, что до банка.
  const expiresAt = Date.parse(state.expires_at);
  if (Number.isFinite(expiresAt) && now >= expiresAt + SBP_EXPIRY_GRACE_MS) {
    return { kind: 'failed', text: sbpFailureText(null) };
  }
  return { kind: 'open' };
}

/** Подпись на экране ожидания, открытом по ссылке или из списка, — кода на нём может и не быть. */
export const SBP_AWAITING_TEXT =
  'Ждём подтверждения банка. Откройте приложение банка и завершите оплату, если ещё не сделали этого.';

/**
 * Отчего платёж не состоялся — словами для плательщика.
 *
 * «Если деньги списались, они вернутся автоматически» — в обоих случаях, потому что в обоих они
 * могли списаться: банк может подтвердить оплату уже после нашего срока, а отказ сервер ставит и
 * тогда, когда заказ на полученные деньги оформить не вышло. Сказать «не списаны» здесь нельзя.
 *
 * Причину отказа сервер теперь пишет для человека, и она показывается как есть.
 */
export function sbpFailureText(state: SbpPaymentState | null): string {
  if (state === null) {
    return 'Банк не подтвердил оплату. Если деньги списались, они вернутся автоматически.';
  }
  const reason = (state.failure_reason ?? '').trim().replace(/[.\s]+$/, '');
  const head = reason.length > 0 ? `Оплата не прошла: ${reason}.` : 'Оплата не прошла.';
  return `${head} Если деньги списались, они вернутся автоматически.`;
}

// ---------- платежи, которые ещё не стали заказом ---------------------------------------------

/**
 * Один платёж из списка ожидающих: ещё не оформленный в заказ или недавно неудачный.
 *
 * Адрес, номер и литры — с сервера, из описания заказа, которое он получил при старте: у клиента
 * на этом устройстве черновика может и не быть, если платили с другого.
 */
export interface SbpPendingPayment {
  id: string;
  status: SbpPaymentStatus;
  amount: string;
  order_id: string | null;
  failure_reason: string | null;
  expires_at: string;
  created_at: string;
  paid_at: string | null;
  refunded_at: string | null;
  address_text: string | null;
  plate: string | null;
  liters: string | null;
}

export function fetchPendingSbpPayments(): Promise<SbpPendingPayment[]> {
  return apiRequest<{ items: SbpPendingPayment[] }>('/v1/consumer/orders/sbp/pending').then(
    (page) => page.items,
  );
}

// ---------- начатый платёж переживает уход с экрана ------------------------------------------
//
// На телефоне «Открыть приложение банка» УВОДИТ вкладку: наша страница выгружается, а вместе с
// ней исчезает и то, что мы завели платёж. Человек платит по-настоящему, возвращается — а
// приложение о платеже не помнит и показывает сводку заново. Нажатие «Оформить и оплатить»
// заводит ВТОРОЙ код на те же деньги, и пока уборщик не вернёт первый, человек оплачен дважды.
//
// Поэтому платёж живёт рядом с черновиком заказа и в том же хранилище: черновик уже обязан
// переживать закрытую вкладку, и по отдельности эти две записи бессмысленны.
//
// **Запись называет СВОЙ черновик — его `externalId` — и без этого имени сводке не годится.**
// Место в хранилище одно, а черновиков за сутки бывает несколько: человек бросил заказ A на коде и
// начал заказ B. Запись от A, прочитанная сводкой B, показывала на ней код и СУММУ ЧУЖОГО выезда,
// и оплата уходила не за тот заказ — ревизия 11.09.2026 воспроизвела это тестом. Поэтому читают
// запись всегда с именем черновика на руках (`loadStartedSbpPayment`), а без имени — только там,
// где платёж опознан иначе: на экране ожидания, куда его идентификатор приходит в адресе.

const STARTED_KEY = 'tt.order.sbp';

/** Столько же, сколько живёт черновик: платёж без черновика оформить всё равно нечем. */
const STARTED_TTL_MS = 24 * 60 * 60 * 1000;

interface StoredStartedPayment {
  savedAt: number;
  /** `externalId` черновика, ради которого платёж заведён. */
  draftId: string;
  payment: StartedSbpPayment;
}

/** Начатый платёж вместе с черновиком, которому он принадлежит. */
export interface StartedSbpRecord {
  /**
   * Чей платёж. `null` — запись от сборки, писавшей платёж без имени черновика: чей он, узнать
   * уже неоткуда, и ни одному черновику она не принадлежит.
   */
  draftId: string | null;
  payment: StartedSbpPayment;
}

export function saveStartedSbpPayment(
  payment: StartedSbpPayment,
  draftId: string,
  now: number = Date.now(),
): void {
  try {
    const stored: StoredStartedPayment = { savedAt: now, draftId, payment };
    localStorage.setItem(STARTED_KEY, JSON.stringify(stored));
  } catch {
    // Хранилище может быть недоступно или переполнено. Тогда экран работает как раньше — из
    // памяти; теряется только возврат из банка, и сказать об этом посреди оплаты нечего.
  }
}

/**
 * Начатый и ещё не оформленный платёж, если он есть и не протух, — вместе с именем его черновика.
 *
 * Запись без `expires_at` — от сборки до того, как заказ стал оформлять сервер; у такого платежа
 * на сервере нет описания заказа, и ждать по нему нечего. Она сбрасывается как протухшая.
 *
 * Запись без `draftId` — от сборки, не связывавшей платёж с черновиком. Она НЕ сбрасывается:
 * платёж у банка от этого не исчезает, и на экране ожидания, где его опознают по идентификатору из
 * адреса, код по-прежнему можно показать. Просто ни один черновик её своей не считает.
 */
export function loadStartedSbpRecord(now: number = Date.now()): StartedSbpRecord | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STARTED_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      clearStartedSbpPayment();
      return null;
    }
    const { savedAt, draftId, payment } = parsed as Partial<StoredStartedPayment>;
    if (typeof savedAt !== 'number' || now - savedAt > STARTED_TTL_MS) {
      clearStartedSbpPayment();
      return null;
    }
    if (
      payment === undefined ||
      typeof payment.id !== 'string' ||
      typeof payment.payload !== 'string' ||
      typeof payment.amount !== 'string' ||
      typeof payment.expires_at !== 'string'
    ) {
      clearStartedSbpPayment();
      return null;
    }
    return {
      draftId: typeof draftId === 'string' && draftId.length > 0 ? draftId : null,
      payment: {
        id: payment.id,
        payload: payment.payload,
        amount: payment.amount,
        expires_at: payment.expires_at,
        qr_image_base64:
          typeof payment.qr_image_base64 === 'string' ? payment.qr_image_base64 : null,
      },
    };
  } catch {
    clearStartedSbpPayment();
    return null;
  }
}

/**
 * Платёж ЭТОГО черновика — и только его.
 *
 * Единственный способ прочитать запись со сводки. Чужая запись отсюда не возвращается и при этом
 * не стирается: за ней живой код у банка, и стереть её значит потерять из виду оплату, по которой
 * сервер ещё может оформить заказ.
 */
export function loadStartedSbpPayment(
  draftId: string,
  now: number = Date.now(),
): StartedSbpPayment | null {
  const record = loadStartedSbpRecord(now);
  if (record === null || record.draftId === null || record.draftId !== draftId) return null;
  return record.payment;
}

export function clearStartedSbpPayment(): void {
  try {
    localStorage.removeItem(STARTED_KEY);
  } catch {
    // Нечего чистить — нечего и сообщать.
  }
}
